import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  Heading1, Heading2, Heading3, List, ListOrdered, AlignLeft,
  AlignCenter, AlignRight, Link as LinkIcon, ImagePlus, Palette,
  Highlighter, Undo, Redo, Type, RemoveFormatting
} from 'lucide-react';
import { VariableSelector } from './VariableSelector';

const COLORS = ['#000000','#E8452E','#2A9D8F','#3B82F6','#8B5CF6','#F59E0B','#EC4899','#6B7280','#FFFFFF'];
const HIGHLIGHTS = ['#FEF08A','#BBF7D0','#BFDBFE','#E9D5FF','#FECACA','#FED7AA'];

const ToolbarButton = ({ onClick, active, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-1.5 rounded transition-colors ${
      active ? 'bg-primary/20 text-primary' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
    }`}
  >
    {children}
  </button>
);

const ColorPicker = ({ colors, onSelect, icon: Icon, title }) => (
  <div className="relative group">
    <button type="button" className="p-1.5 rounded text-muted-foreground hover:bg-muted hover:text-foreground" title={title}>
      <Icon size={16} />
    </button>
    <div className="absolute top-full left-0 mt-1 hidden group-hover:flex flex-wrap gap-1 p-2 bg-popover border border-border rounded-lg shadow-lg z-50 w-[120px]">
      {colors.map(c => (
        <button
          key={c}
          type="button"
          onClick={() => onSelect(c)}
          className="w-5 h-5 rounded border border-border/50 hover:scale-110 transition-transform"
          style={{ backgroundColor: c }}
        />
      ))}
    </div>
  </div>
);

// Image toolbar that shows when image is selected
const ImageToolbar = ({ editor }) => {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!editor) return;
    const handleSelectionUpdate = () => {
      const { state } = editor;
      const { selection } = state;
      const node = state.doc.nodeAt(selection.from);
      if (node?.type.name === 'image') {
        // Find the DOM element for the image
        const dom = editor.view.nodeDOM(selection.from);
        if (dom) {
          const rect = dom.getBoundingClientRect();
          const editorRect = editor.view.dom.closest('.email-editor')?.getBoundingClientRect();
          if (editorRect) {
            setPos({
              top: rect.top - editorRect.top - 40,
              left: rect.left - editorRect.left + rect.width / 2 - 150,
            });
          }
        }
        setShow(true);
      } else {
        setShow(false);
      }
    };
    editor.on('selectionUpdate', handleSelectionUpdate);
    return () => editor.off('selectionUpdate', handleSelectionUpdate);
  }, [editor]);

  const setImageWidth = (width) => {
    if (!editor) return;
    const { state } = editor;
    const node = state.doc.nodeAt(state.selection.from);
    if (node?.type.name === 'image') {
      editor.chain().focus().setImage({ ...node.attrs, width }).run();
    }
  };

  const addLinkToImage = () => {
    if (!editor) return;
    const url = window.prompt('URL du lien pour cette image :');
    if (!url) return;
    // Select the image node, then wrap in link
    const { state } = editor;
    const { selection } = state;
    const from = selection.from;
    const to = selection.to || from + 1;
    editor.chain().focus().setTextSelection({ from, to }).setLink({ href: url }).run();
  };

  if (!show) return null;

  return (
    <div
      className="absolute z-50 flex items-center gap-1 bg-popover border border-border rounded-lg shadow-lg p-1.5"
      style={{ top: Math.max(0, pos.top), left: Math.max(0, pos.left) }}
    >
      {[
        { label: 'Petit', w: '200px' },
        { label: 'Moyen', w: '400px' },
        { label: 'Grand', w: '600px' },
        { label: '100%', w: '100%' },
      ].map(s => (
        <button key={s.w} type="button" onClick={() => setImageWidth(s.w)}
          className="text-xs px-2 py-1 rounded hover:bg-muted text-foreground whitespace-nowrap">
          {s.label}
        </button>
      ))}
      <div className="w-px h-5 bg-border mx-0.5" />
      <button type="button" onClick={addLinkToImage}
        className="text-xs px-2 py-1 rounded hover:bg-muted text-foreground flex items-center gap-1">
        <LinkIcon size={12} /> Lien
      </button>
    </div>
  );
};

const EmailEditor = ({ value, onChange, placeholder }) => {
  const debounceRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      TextStyle,
      Color,
      Highlight.configure({ multicolor: true }),
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: { class: 'email-img' },
      }),
      Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Placeholder.configure({ placeholder: placeholder || 'Rédigez votre email...' }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        onChange(editor.getHTML());
      }, 300);
    },
  });

  useEffect(() => {
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, []);

  const handleInsertVariable = useCallback((variable) => {
    editor?.chain().focus().insertContent(variable).run();
  }, [editor]);

  const addImage = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        editor?.chain().focus().setImage({ src: reader.result }).run();
      };
      reader.readAsDataURL(file);
    };
    input.click();
  }, [editor]);

  const addImageUrl = useCallback(() => {
    const url = window.prompt("URL de l'image :");
    if (url) editor?.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  const addLink = useCallback(() => {
    const prev = editor?.getAttributes('link')?.href || '';
    const url = window.prompt('URL du lien :', prev);
    if (url === null) return;
    if (url === '') {
      editor?.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }, [editor]);

  if (!editor) return (
    <div className="min-h-[200px] border border-border rounded-lg flex items-center justify-center text-muted-foreground">
      Chargement...
    </div>
  );

  return (
    <div className="email-editor space-y-3 relative">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-foreground">Contenu de l'email</span>
        <VariableSelector onSelect={handleInsertVariable} />
      </div>

      {/* Toolbar */}
      <div className="border border-border rounded-t-lg bg-muted/30 p-1.5 flex flex-wrap items-center gap-0.5">
        <ToolbarButton onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} title="Gras">
          <Bold size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} title="Italique">
          <Italic size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleUnderline().run()} active={editor.isActive('underline')} title="Souligné">
          <UnderlineIcon size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} title="Barré">
          <Strikethrough size={16} />
        </ToolbarButton>

        <div className="w-px h-5 bg-border mx-1" />

        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive('heading', { level: 1 })} title="Titre 1">
          <Heading1 size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} title="Titre 2">
          <Heading2 size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} title="Titre 3">
          <Heading3 size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setParagraph().run()} active={editor.isActive('paragraph')} title="Paragraphe">
          <Type size={16} />
        </ToolbarButton>

        <div className="w-px h-5 bg-border mx-1" />

        <ToolbarButton onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} title="Liste à puces">
          <List size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} title="Liste numérotée">
          <ListOrdered size={16} />
        </ToolbarButton>

        <div className="w-px h-5 bg-border mx-1" />

        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('left').run()} active={editor.isActive({ textAlign: 'left' })} title="Gauche">
          <AlignLeft size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('center').run()} active={editor.isActive({ textAlign: 'center' })} title="Centrer">
          <AlignCenter size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().setTextAlign('right').run()} active={editor.isActive({ textAlign: 'right' })} title="Droite">
          <AlignRight size={16} />
        </ToolbarButton>

        <div className="w-px h-5 bg-border mx-1" />

        <ColorPicker colors={COLORS} onSelect={(c) => editor.chain().focus().setColor(c).run()} icon={Palette} title="Couleur" />
        <ColorPicker colors={HIGHLIGHTS} onSelect={(c) => editor.chain().focus().toggleHighlight({ color: c }).run()} icon={Highlighter} title="Surligner" />

        <div className="w-px h-5 bg-border mx-1" />

        <ToolbarButton onClick={addLink} active={editor.isActive('link')} title="Lien">
          <LinkIcon size={16} />
        </ToolbarButton>

        <div className="relative group">
          <button type="button" className="p-1.5 rounded text-muted-foreground hover:bg-muted hover:text-foreground" title="Image">
            <ImagePlus size={16} />
          </button>
          <div className="absolute top-full right-0 mt-1 hidden group-hover:flex flex-col gap-1 p-2 bg-popover border border-border rounded-lg shadow-lg z-50 min-w-[160px]">
            <button type="button" onClick={addImage} className="text-left text-sm px-2 py-1.5 rounded hover:bg-muted w-full">
              Uploader une image
            </button>
            <button type="button" onClick={addImageUrl} className="text-left text-sm px-2 py-1.5 rounded hover:bg-muted w-full">
              Image depuis URL
            </button>
          </div>
        </div>

        <div className="w-px h-5 bg-border mx-1" />

        <ToolbarButton onClick={() => editor.chain().focus().undo().run()} title="Annuler">
          <Undo size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().redo().run()} title="Rétablir">
          <Redo size={16} />
        </ToolbarButton>
        <ToolbarButton onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} title="Effacer formatage">
          <RemoveFormatting size={16} />
        </ToolbarButton>
      </div>

      {/* Editor + Image toolbar */}
      <div className="border border-border border-t-0 rounded-b-lg relative">
        <ImageToolbar editor={editor} />
        <EditorContent editor={editor} className="tiptap-editor" />
      </div>

      <style>{`
        .tiptap-editor .tiptap {
          min-height: 220px;
          padding: 1rem;
          outline: none;
          font-size: 14px;
          color: hsl(var(--foreground));
        }
        .tiptap-editor .tiptap p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: hsl(var(--muted-foreground));
          pointer-events: none;
          float: left;
          height: 0;
        }
        .tiptap-editor .tiptap h1 { font-size: 1.75rem; font-weight: 700; margin: 0.5em 0; }
        .tiptap-editor .tiptap h2 { font-size: 1.35rem; font-weight: 600; margin: 0.4em 0; }
        .tiptap-editor .tiptap h3 { font-size: 1.1rem; font-weight: 600; margin: 0.3em 0; }
        .tiptap-editor .tiptap ul { list-style: disc; padding-left: 1.5rem; }
        .tiptap-editor .tiptap ol { list-style: decimal; padding-left: 1.5rem; }
        .tiptap-editor .tiptap a { color: hsl(var(--primary)); text-decoration: underline; cursor: pointer; }
        .tiptap-editor .tiptap img.email-img {
          max-width: 100%;
          height: auto;
          border-radius: 0.375rem;
          margin: 0.5rem 0;
          cursor: pointer;
          transition: outline 0.15s;
        }
        .tiptap-editor .tiptap img.email-img.ProseMirror-selectednode {
          outline: 2px solid hsl(var(--primary));
          outline-offset: 2px;
        }
        .tiptap-editor .tiptap mark { border-radius: 0.15rem; padding: 0.1rem 0.15rem; }
      `}</style>
    </div>
  );
};

export default EmailEditor;
